// src/components/FittingPayment.js

import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import "../style/FittingPayment.css";
import { useTranslation } from "react-i18next";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Cloud Function URLs
const CREATE_PAYMENT_INTENT_URL =
  "https://us-central1-mcknight-homme.cloudfunctions.net/createFittingPaymentIntent";
const CONFIRM_APPOINTMENT_URL =
  "https://us-central1-mcknight-homme.cloudfunctions.net/confirmFittingAppointment";

const DEPOSIT_AMOUNT = "€50";
const DEPOSIT_BUTTON_TEXT = "Pay €50";

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      fontFamily: "Georgia, serif",
      color: "#333333",
      "::placeholder": {
        color: "#aaaaaa",
      },
    },
    invalid: {
      color: "#dc2626",
      iconColor: "#dc2626",
    },
  },
  hidePostalCode: true,
};

// Payment Form Component
const PaymentForm = ({ clientSecret, appointmentId, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setErrorMessage(event.error.message);
    } else {
      setErrorMessage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setErrorMessage("Card form not loaded. Please refresh and try again.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        },
      );

      if (error) {
        setErrorMessage(error.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Confirm the appointment in backend
        const confirmResponse = await fetch(CONFIRM_APPOINTMENT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            appointmentId,
            paymentIntentId: paymentIntent.id,
          }),
        });

        const confirmData = await confirmResponse.json();

        if (confirmData.success) {
          onSuccess(confirmData.appointment);
        } else {
          setErrorMessage(confirmData.error || "Failed to confirm appointment");
        }
      }
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="fp-payment-form">
      <div className="fp-amount-display">
        <span className="fp-amount-label">{t("Deposit Amount")}</span>
        <span className="fp-amount-value">{DEPOSIT_AMOUNT}</span>
      </div>

      <p className="fp-deposit-note">
        {t("This deposit will be deducted from the cost of your outfit.")}
      </p>

      <div className="fp-card-element-container">
        <label className="fp-card-label">{t("Card Details")}</label>
        <div className="fp-card-element-wrapper">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={handleCardChange}
          />
        </div>
        <div className="fp-stripe-badge">
          <svg
            className="fp-lock-icon"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="3"
              y="11"
              width="18"
              height="11"
              rx="2"
              stroke="#6B7280"
              strokeWidth="2"
            />
            <path
              d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
              stroke="#6B7280"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span>{t("Secured by")}</span>
          <span className="fp-stripe-text">stripe</span>
        </div>
      </div>

      {errorMessage && <div className="fp-error-message">{errorMessage}</div>}

      <div className="fp-button-row">
        <button
          type="button"
          className="fp-cancel-button"
          onClick={onCancel}
          disabled={isProcessing}
        >
          {t("Cancel")}
        </button>
        <button
          type="submit"
          className="fp-pay-button"
          disabled={!stripe || !cardComplete || isProcessing}
        >
          {isProcessing ? t("Processing...") : t(DEPOSIT_BUTTON_TEXT)}
        </button>
      </div>
    </form>
  );
};

// Main Payment Component with Elements wrapper
const FittingPayment = ({
  appointmentData,
  customerInfo,
  productInfo,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Create payment intent when component mounts
    const createPaymentIntent = async () => {
      try {
        console.log("Creating payment intent with product:", productInfo);

        const response = await fetch(CREATE_PAYMENT_INTENT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            // Appointment info
            appointmentDate: appointmentData.dateFormatted,
            appointmentTime: appointmentData.slot,
            appointmentStartTime: appointmentData.startTime,
            eventTypeUri: appointmentData.eventTypeUri,

            // Customer info
            customerEmail: customerInfo.email,
            customerName: customerInfo.name,
            customerPhone: customerInfo.phone,
            eventTypeUri: appointmentData.eventTypeUri,

            // Product info
            productId: productInfo?.productId || "",
            productName: productInfo?.name || "",
            productPrice: productInfo?.price || "",
            productImage: productInfo?.image || "",
          }),
        });

        const data = await response.json();
        console.log("Payment intent response:", data);

        if (data.success) {
          setClientSecret(data.clientSecret);
          setAppointmentId(data.appointmentId);
        } else {
          setError(data.error || "Failed to initialize payment");
        }
      } catch (err) {
        console.error("Error creating payment intent:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    createPaymentIntent();
  }, [appointmentData, customerInfo, productInfo]);

  if (isLoading) {
    return (
      <div className="fitting-payment">
        <div className="fp-loading">
          <div className="fp-spinner"></div>
          <p>{t("Setting up payment...")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fitting-payment">
        <div className="fp-error">
          <p>
            {t("Error")}: {error}
          </p>
          <button onClick={onCancel} className="fp-cancel-button">
            {t("Go Back")}
          </button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  const options = {
    clientSecret,
  };

  return (
    <div className="fitting-payment">
      <div className="fp-header">
        <h3>{t("Complete Your Booking")}</h3>
      </div>

      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          clientSecret={clientSecret}
          appointmentId={appointmentId}
          onSuccess={onSuccess}
          onCancel={onCancel}
        />
      </Elements>
    </div>
  );
};

export default FittingPayment;
